/**
 * WGMinutes Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * Displays working group meeting minutes in a consultation-style card layout with search,
 * year filtering, and pagination. Matches the design and functionality of ConsultationCards.
 * 
 * Features:
 * - Consultation-style card grid layout
 * - Year dropdown filter
 * - Real-time search with highlighting
 * - Pagination controls
 * - PDF modal integration
 * - Professional consultation design
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of meeting minute objects
 * @returns {JSX.Element} The rendered working group minutes component
 * 
 * @example
 * <WGMinutes data={meetingMinutesArray} />
 */

import React, { useState, useMemo, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './WGMinutes.css';
import PDFModal from '../../modals/PDFModal';
import { truncateText, formatDate, normalizeText } from '../../utils/textUtils';
import './WGMinutesCards.css';
import '../PolicyRegulatory/ConsultationCards.css';

function WGMinutes({ data }) {
  const [modals, setModals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const itemsPerPage = 6;

  // Safety check for data
  const safeData = data || [];

  // Get available years
  const availableYears = useMemo(() => {
    if (!safeData.length) return [];
    const years = [...new Set(safeData.map(item => parseInt(item.year)))].sort((a, b) => b - a);
    return years;
  }, [safeData]);

  // Set default year to show ALL on first load
  const [selectedYear, setSelectedYear] = useState('ALL');

  // Filter data by year and search query
  const filteredData = useMemo(() => {
    return safeData.filter(item => {
      // Filter by selected year (convert both to numbers for proper comparison)
      const yearMatch = selectedYear === 'ALL' || parseInt(item.year) === parseInt(selectedYear);
      
      // Filter by search query
      const searchMatch = searchQuery === '' || 
        normalizeText(item.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        normalizeText(item.summary || '').toLowerCase().includes(searchQuery.toLowerCase());
      
      return yearMatch && searchMatch;
    }).sort((a, b) => {
      // Sort by date (newest first)
      const dateA = new Date(a.publishDate || `${a.year}-${a.month}-01`);
      const dateB = new Date(b.publishDate || `${b.year}-${b.month}-01`);
      return dateB - dateA;
    });
  }, [safeData, selectedYear, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  /**
   * Handles search input changes
   */
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  /**
   * Handles page changes
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      // Scroll to top of content
      document.querySelector('.wg-minutes-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * Handles year selection changes
   */
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value === 'ALL' ? 'ALL' : parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing year
  };

  /**
   * Open a PDF modal or bring existing modal to front
   */
  const openPDFModal = (id, title, pdfUrl, minuteData = null) => {
    const existingModalIndex = modals.findIndex((modal) => modal.id === id);
    const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

    if (existingModalIndex !== -1) {
      const updatedModals = [...modals];
      updatedModals[existingModalIndex].isOpen = true;
      updatedModals[existingModalIndex].zIndex = maxZIndex;
      setModals(updatedModals);
    } else {
      const newModal = {
        id,
        title,
        pdfUrl,
        minuteData,
        isOpen: true,
        top: `${15 + (modals.length * 2)}%`,
        left: `${30 + (modals.length * 2)}%`,
        zIndex: maxZIndex,
      };
      setModals((prevModals) => [...prevModals, newModal]);
    }
  };

  /**
   * Close a PDF modal by ID
   */
  const closePDFModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    );
  };

  // Show loading state if data is null/undefined
  if (!data) {
    return (
      <section id="WGMinutes" className="wg-minutes-section">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading minutes...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="WGMinutes" className="wg-minutes-section">
      {/* Filter Controls */}
      <div className="wg-filters-status">
        <div className="minutes-controls">
          <div className="minutes-filters">
            <div className="wg-search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search minutes..."
                value={searchQuery}
                onChange={handleSearch}
                className="filtersearch"
              />
              {searchQuery && (
                <button
                  className="clear-search"
                  onClick={() => {
                    setSearchQuery('');
                    setCurrentPage(1);
                  }}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            <div className="year-filter ">
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="year-dropdown"
              >
                <option value="ALL">Year (ALL)</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="minutes-stats-panel">
            <span className="results-count">
              <i className="fas fa-chart-bar"></i>
              {filteredData.length} minute record{filteredData.length > 1 ? 's' : ''} found
            </span>
            {(searchQuery || selectedYear !== 'ALL') && safeData.length > 0 && (
              <span className="filter-indicator">
                {safeData.length} total
              </span>
            )}
          </div>

          <div className="view-controls">
            <button
              className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              <i className="fas fa-th-large"></i>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Minutes Content */}
      {filteredData.length === 0 ? (
        <div className="no-results">
          <div className="no-results-content">
            <i className="fas fa-search"></i>
            <h3>No minutes found</h3>
          </div>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="consultation-cards-container">
          <div className="consultation-cards-grid">
            {paginatedData.map((item) => (
              <div
                key={item.id}
                className="consultation-card"
                onClick={() => item.fileName && openPDFModal(item.id, item.title, item.fileName, item)}
                style={{ cursor: item.fileName ? 'pointer' : 'default' }}
              >
                <div className="consultation-card-date-header">
                  <div className="consultation-icon">
                    <i className="fas fa-file-pdf"></i>
                  </div>
                  <div className="completion-date-top">
                    <i className="fas fa-calendar-alt"></i>
                    <span>{item.month} {item.year}</span>
                  </div>
                </div>

                <div className="consultation-card-content">
                  <Tippy content={normalizeText(item.title)}>
                    <h3 className="consultation-card-title">
                      <span className="title-text">
                        {searchQuery.trim() ? (
                          <span dangerouslySetInnerHTML={{ 
                            __html: truncateText(normalizeText(item.title), 65).replace(
                              new RegExp(searchQuery.trim(), 'gi'), 
                              match => `<span class="highlight-match">${match}</span>`
                            )
                          }} />
                        ) : (
                          truncateText(normalizeText(item.title), 65)
                        )}
                      </span>
                    </h3>
                  </Tippy>

                  {item.summary && item.summary.trim() && (
                    <div className="consultation-card-summary">
                      <Tippy content={normalizeText(item.summary)}>
                        <p>
                          {searchQuery.trim() ? (
                            <span dangerouslySetInnerHTML={{ 
                              __html: truncateText(normalizeText(item.summary), 180).replace(
                                new RegExp(searchQuery.trim(), 'gi'), 
                                match => `<span class="highlight-match">${match}</span>`
                              )
                            }} />
                          ) : (
                            truncateText(normalizeText(item.summary), 180)
                          )}
                        </p>
                      </Tippy>
                    </div>
                  )}
                </div>

                <div className="consultation-card-footer">
                  <div className="consultation-card-tags">
                    <span className="consultation-tag date-tag consultation-tag-light-gray">
                      {item.month} {item.year}
                    </span>
                    <span className="consultation-tag status-tag consultation-tag-consultation">
                      <i className="fas fa-file-pdf"></i>
                      PDF
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="minutes-list">
          {paginatedData.map((meeting) => (
            <div 
              key={meeting.id} 
              className="meeting-list-item"
              onClick={() => meeting.fileName && openPDFModal(meeting.id, meeting.title, meeting.fileName, meeting)}
              style={{ cursor: meeting.fileName ? 'pointer' : 'default' }}
            >
              <div className="meeting-list-header-section">
                <div className="list-item-icon">
                  <i className="fas fa-file-pdf"></i>
                </div>
                <div className="list-item-date">
                  <i className="fas fa-calendar-alt"></i>
                  <span>{meeting.month} {meeting.year}</span>
                </div>
              </div>

              <div className="meeting-list-content">
                <div className="meeting-list-main">
                  <h3 className="meeting-list-title">
                    {searchQuery.trim() ? (
                      <span dangerouslySetInnerHTML={{ 
                        __html: normalizeText(meeting.title).replace(
                          new RegExp(searchQuery.trim(), 'gi'), 
                          match => `<span class="highlight-match">${match}</span>`
                        )
                      }} />
                    ) : (
                      normalizeText(meeting.title)
                    )}
                  </h3>
                  
                  {meeting.summary && meeting.summary.trim() && (
                    <p className="meeting-list-summary">
                      {searchQuery.trim() ? (
                        <span dangerouslySetInnerHTML={{ 
                          __html: normalizeText(meeting.summary).replace(
                            new RegExp(searchQuery.trim(), 'gi'), 
                            match => `<span class="highlight-match">${match}</span>`
                          )
                        }} />
                      ) : (
                        normalizeText(meeting.summary)
                      )}
                    </p>
                  )}
                </div>
              </div>

              <div className="meeting-list-footer">
                <div className="meeting-list-tags">
                  <span className="consultation-tag date-tag consultation-tag-light-gray">
                    {meeting.month} {meeting.year}
                  </span>
                  <span className="consultation-tag status-tag consultation-tag-consultation">
                    <i className="fas fa-file-pdf"></i>
                    PDF
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <div className="pagination">
            <button
              className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            
            <button
              className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
            >
              <i className="fas fa-angle-left"></i>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </button>
            ))}

            <button
              className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <i className="fas fa-angle-right"></i>
            </button>

            <button
              className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>

     
        </div>
      )}
      
      {/* PDF Modals */}
      {modals.map((modal) => (
        <PDFModal
          key={modal.id}
          title={modal.title}
          url={modal.pdfUrl}
          show={modal.isOpen}
          onClose={(e) => {
            e.stopPropagation();
            closePDFModal(modal.id);
          }}
          allowFullscreen={true}
          allowMove={true}
          showLinkinToolBar={true}
          style={{ zIndex: modal.zIndex }}
          minuteData={modal.minuteData}
        />
      ))}
    </section>
  );
}

export default WGMinutes;